import { Injectable } from '@angular/core';

import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { BaseResponse } from '../models/rest/baseResponse';
import { AppNotificationsService } from '@libs/portal-common/system';
import { VideoRecordSettings } from '../models/videoRecordsSettings';
import { ICallCenterSMSSettings } from '../models/rest/callCenter/callCenterSmsSettings';

export const DEFAULT_AUDIO_RECORD_FILENAME_TEMPLATE = 'IncidentID.SessionID.Prepend';
export const AUDIO_RECORD_FILENAME_TEMPLATE_CHIPS = ['IncidentID', 'SessionID', 'Prepend', 'IncidentDateTime'];

export interface ICallCenter {
  Id?: number;
  CallCenterName: string;
  IsActive: boolean;
  APIKey: string;
  TimeZone: string;
  modified?: Date;

  fkRegion: number;
  AutoAnswerCalls: boolean;

  QueueOkSeconds?: number;
  QueueWarningSeconds?: number;
  HangUpOkSeconds?: number;
  HangUpWarningSeconds?: number;
  WrapUpOkSeconds?: number;
  WrapUpWarningSeconds?: number;

  IsAuthCodeSendingEnabled: boolean;
  MandatoryIssue: boolean;
  AllowIncidentWithoutCall: boolean;
  AudioRecordFilenameTemplate: string;
  MobilePhoneAccountId?: string;

  SmsSignatureEnabled?: boolean;
  SmsSignatureText?: string;
  SmsPrivacyPolicyEnabled?: boolean;
  SmsPrivacyPolicyUrl?: string;
}

export class CreateCallCenterModel {
  callCenter: ICallCenter;
  sourceRegionId: number;

  videoRecordsSettings: VideoRecordSettings;

  constructor() {
    this.callCenter = {
      CallCenterName: null,
      IsActive: true,
      APIKey: null,
      TimeZone: null,
      fkRegion: null,
      AutoAnswerCalls: false,
      IsAuthCodeSendingEnabled: true,
      MandatoryIssue: false,
      AllowIncidentWithoutCall: false,
      AudioRecordFilenameTemplate: DEFAULT_AUDIO_RECORD_FILENAME_TEMPLATE,
      MobilePhoneAccountId: null,
    };

    this.videoRecordsSettings = null;
  }
}

export interface IGetCallCenterResponse extends BaseResponse {
  CallCenter: ICallCenter;
}

export interface IGetCallCentersListResponse extends BaseResponse {
  CallCenters: Array<ICallCenter>;
}

export interface GetCallCenterTypes extends BaseResponse {
  Types: Array<string>;
}

export interface GetCallCenterSettingsResponse extends BaseResponse {
  Type: string;
  SystemLogLevel: string;
}

export interface GetLogLevels extends BaseResponse {
  Levels: Array<string>;
}

export interface GetSystemLogLevelResponse extends BaseResponse {
  Type: string;
}

@Injectable({ providedIn: 'root' })
export class CallCenterService {
  constructor(
    private _httpClient: HttpClient,
    private notifications: AppNotificationsService,
  ) {}

  getAll(): Observable<IGetCallCentersListResponse> {
    return this._httpClient.get<IGetCallCentersListResponse>('CallCenter');
  }

  getCallCenter(id: number): Observable<IGetCallCenterResponse> {
    return this._httpClient.get<IGetCallCenterResponse>(`CallCenter/${id}`).pipe(
      tap((response) => {
        this.notifications.checkResponse(response);
      }),
    );
  }

  update(model: ICallCenter): Observable<any> {
    return this._httpClient.put<any>('CallCenter', { CallCenter: model });
  }

  updateSmsSettings(model: ICallCenterSMSSettings): Observable<IGetCallCenterResponse> {
    return this._httpClient.put<IGetCallCenterResponse>('CallCenter/SmsSettings', { CallCenter: model });
  }

  insert(model: CreateCallCenterModel): Observable<any> {
    return this._httpClient.post<any>('CallCenter', model);
  }

  validateCreateCallCenterStep(model: CreateCallCenterModel): Observable<any> {
    return this._httpClient.post<any>('CallCenter/ValidateCreateCallCenterStep', model);
  }

  validateCopyRegionDataStep(model: CreateCallCenterModel): Observable<any> {
    return this._httpClient.post<any>('CallCenter/ValidateCopyRegionDataStep', model);
  }

  getCallCenterTypes() {
    return this._httpClient.get<GetCallCenterTypes>('CallCenter/Settings/Type');
  }

  changeCallCenterType(type: string) {
    return this._httpClient.put<BaseResponse>('CallCenter/Settings/Type', { Type: type });
  }

  getCurrentSettings() {
    return this._httpClient.get<GetCallCenterSettingsResponse>('CallCenter/Settings');
  }

  getLogLevels() {
    return this._httpClient.get<GetLogLevels>('CallCenter/Settings/LogLevels');
  }

  changeSystemLogLevel(level: string) {
    return this._httpClient.put<BaseResponse>('CallCenter/Settings/LogLevels', { SystemLogLevel: level });
  }

  delete(id: number): Observable<any> {
    let params = new HttpParams().set('id', id.toString());

    return this._httpClient.delete<any>('CallCenter', { params: params });
  }
}
